import { Modal } from "@/components/molecules/Modals/ModalController";
import { storeSignupContext } from "@/events/signupTracking";
import { PayloadAction, createSlice } from "@reduxjs/toolkit";
import { MESSAGE_SLICE_NAME } from "@repo/shared/redux";

import { PopupMessage } from "../../components/atoms/PopupMessage/PopupMessage.type";
import { IRootState } from "../store";

/**
 * The dialogs that can be raised by dispatching `showDialog`, rendered by
 * ModalControllerV2.
 *
 * Kept in step with the same type in @repo/shared: both slices are named with
 * MESSAGE_SLICE_NAME, so shared code dispatching `showDialog` lands here.
 */

export type DialogType =
  | "premium-subscription"
  /** No club imported yet — prerequisite gate, not a paywall. */
  | "club-import-gate"
  /** In-context pitch for a first import, dismissible with a cool-off. */
  | "club-import-nudge"
  /** Club imported but the balance is spent — the upgrade moment. */
  | "out-of-credits"
  /** Sign-up bonus just ran out — the first time credits are ever named. */
  | "credits-intro"
  | "empty-club"
  /** The import is running — a blocking overlay, raised from any CTA. */
  | "now-importing"
  /** The extension is behind the latest release — warn before an import CTA proceeds. */
  | "extension-update-notice"
  /** The Chrome extension just completed an SBC set — ask for a store rating. */
  | "extension-review";

/** A dialog to open, plus whatever that dialog needs to render. */
export type DialogConfig = {
  modalType: DialogType;
  /** Generic data field for modal-specific data */
  data?: any;
};

type MessageState = {
  message: PopupMessage;
  modal: Modal;
  dialog: DialogConfig | null;
  alerts: Alert[];
};

export type Alert = {
  type: "success" | "error" | "warning" | "info";
  title?: string;
  description: string;
  id: string;
  persistent?: boolean;
  link?: { label: string; to: string };
};

const initialState: MessageState = {
  message: null,
  modal: null,
  dialog: null,
  alerts: [],
};

const messageSlice = createSlice({
  // Shared, not a literal: shared code's `showDialog` only reaches this reducer
  // while both slices produce the same action prefix. See @repo/shared.
  name: MESSAGE_SLICE_NAME,
  initialState,
  reducers: {
    setMessage(state, action) {
      state.message = action.payload;
    },
    clearMessage(state) {
      state.message = null;
    },
    showModal(state, action: PayloadAction<Modal | null>) {
      state.modal = action.payload;
      // Store signup context when showing login-required modal
      if (action.payload?.modalType === "login-required") {
        const trigger =
          action.payload.freeFeatureLoggedInRequiredHook || "login-required";
        storeSignupContext(trigger);
      } else if (action.payload?.modalType === "sign-in-to-subscribe") {
        storeSignupContext("sign-in-to-subscribe");
      }
    },
    showDialog(state, action: PayloadAction<DialogConfig>) {
      state.dialog = action.payload;
    },
    hideDialog(state) {
      state.dialog = null;
    },
    addAlert(state, action: PayloadAction<Omit<Alert, "id">>) {
      const alert = {
        ...action.payload,
        id: action.payload.title + action.payload.description + Date.now(),
      };
      state.alerts = [...state.alerts, alert];
    },
    removeAlert(state, action: PayloadAction<string>) {
      state.alerts = state.alerts.filter(
        (alert) => alert.id !== action.payload,
      );
    },
  },
});

export const getMessageSelector = (state: IRootState) => state.messageReducer;
export const dialogSelector = (state: IRootState) =>
  state.messageReducer.dialog;
export const {
  setMessage,
  clearMessage,
  showModal,
  showDialog,
  hideDialog,
  addAlert,
  removeAlert,
} = messageSlice.actions;
export default messageSlice.reducer;
